import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ForkKnife, TrendUp, TrendDown, Minus, ArrowsClockwise, Copy, Lightbulb } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { api } from "../context/AuthContext";

const DIR_META = {
  su: { icon: TrendUp, color: "#34D399", label: "in crescita" },
  giu: { icon: TrendDown, color: "#F87171", label: "in calo" },
  stabile: { icon: Minus, color: "#94A3B8", label: "stabile" },
};

const copyPromo = (p) => {
  navigator.clipboard.writeText(`${p.titolo} — ${p.descrizione} (Canale: ${p.canale}, quando: ${p.quando})`)
    .then(() => toast.success("Idea promo copiata"))
    .catch(() => toast.error("Copia non riuscita"));
};

export const FoodObservatory = () => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    setError(false);
    try {
      const { data } = await api.get(`/viki/food-observatory${refresh ? "?refresh=1" : ""}`);
      setDoc(data);
      if (refresh) toast.success("Osservatorio rigenerato da VIKI");
    } catch {
      setError(true);
      if (refresh) toast.error("Rigenerazione non riuscita, riprova");
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const d = doc?.data;
  return (
    <div className="p-8 rounded-lg bg-surface border border-surface-border animate-fade-up mt-8" data-testid="food-observatory">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <ForkKnife size={20} weight="duotone" className="text-amber-400" />
            <h2 className="font-heading text-2xl font-semibold tracking-tight">Osservatorio Food</h2>
            {doc?.mese && (
              <span data-testid="food-month-badge" className="text-[10px] tracking-[0.2em] uppercase text-amber-300/80 bg-amber-400/10 border border-amber-400/20 rounded-full px-3 py-1">
                {doc.mese}
              </span>
            )}
          </div>
          <p className="text-sm text-slate-400 mt-1.5">Trend stagionali e idee promo pronte per michelchef.it — a cura di V.I.K.I.</p>
        </div>
        <Button data-testid="food-refresh-button" onClick={() => load(true)} disabled={loading}
          variant="ghost" className="h-9 px-4 border border-white/20 text-white hover:bg-white/5 hover:text-white rounded-full text-xs font-semibold">
          <ArrowsClockwise size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> {loading ? "VIKI sta elaborando..." : "Rigenera"}
        </Button>
      </div>

      {loading && !d && (
        <p className="text-sm text-white/40 py-8 text-center" data-testid="food-loading">VIKI sta preparando il quadro food del mese...</p>
      )}
      {error && !d && (
        <p className="text-sm text-white/40 py-8 text-center" data-testid="food-error">
          Osservatorio non disponibile al momento. <button onClick={() => load()} className="underline text-white/70">Riprova</button>
        </p>
      )}

      {d && (
        <>
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/35 mb-2">Ingredienti di stagione</p>
          <div className="flex flex-wrap gap-2 mb-6" data-testid="food-ingredients">
            {(d.ingredienti || []).map((ing) => (
              <span key={ing.nome} title={ing.nota}
                className="text-xs text-white/80 bg-white/5 border border-white/10 rounded-full px-3 py-1.5">
                {ing.nome}<span className="text-white/35"> · {ing.nota}</span>
              </span>
            ))}
          </div>

          <p className="text-[10px] tracking-[0.25em] uppercase text-white/35 mb-2">Trend del mese</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6" data-testid="food-trends">
            {(d.trend || []).map((t) => {
              const m = DIR_META[t.direzione] || DIR_META.stabile;
              const Icon = m.icon;
              return (
                <div key={t.titolo} className="p-4 rounded-md bg-white/[0.03] border border-white/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon size={15} weight="bold" style={{ color: m.color }} />
                    <span className="text-sm font-semibold text-white/90">{t.titolo}</span>
                    <span className="text-[10px] uppercase tracking-wider" style={{ color: m.color }}>{m.label}</span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">{t.perche}</p>
                </div>
              );
            })}
          </div>

          <p className="text-[10px] tracking-[0.25em] uppercase text-white/35 mb-2">Idee promo per michelchef.it</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="food-promos">
            {(d.promo || []).map((p) => (
              <div key={p.titolo} className="p-4 rounded-md bg-amber-400/[0.04] border border-amber-400/15 flex flex-col gap-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-white/90">{p.titolo}</span>
                  <button onClick={() => copyPromo(p)} title="Copia idea"
                    data-testid={`food-promo-copy-${(p.titolo || "").slice(0, 12)}`}
                    className="text-white/40 hover:text-white/80 transition-colors shrink-0">
                    <Copy size={14} />
                  </button>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed">{p.descrizione}</p>
                <div className="flex flex-wrap gap-2 mt-auto pt-1">
                  <span className="text-[10px] uppercase tracking-wider text-amber-300/80 bg-amber-400/10 rounded-full px-2.5 py-0.5">{p.canale}</span>
                  <span className="text-[10px] text-white/40">{p.quando}</span>
                </div>
              </div>
            ))}
          </div>

          {d.consiglio && (
            <div className="flex items-start gap-2.5 mt-6 pt-4 border-t border-white/10" data-testid="food-viki-tip">
              <Lightbulb size={16} weight="duotone" className="text-cyan-300 mt-0.5 shrink-0" />
              <p className="text-sm text-slate-300 italic">"{d.consiglio}" — V.I.K.I.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};
